import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Alert, 
  CircularProgress, 
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  AppBar,
  Toolbar
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import ResourceTile from './components/ResourceTile';
import CategoryFilter from './components/CategoryFilter';
import { resourcesService } from '../../services/resourcesService';

const Resources = () => {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // PWA PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentPdfFilename, setCurrentPdfFilename] = useState('');

  // Load documents on component mount
  useEffect(() => {
    loadDocuments();
  }, []);

  // Filter documents when category changes
  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredDocuments(documents);
    } else {
      setFilteredDocuments(documents.filter(doc => doc.category === selectedCategory));
    }
  }, [documents, selectedCategory]);

  // Auto-refresh documents every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadDocuments(true); // Silent refresh
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const loadDocuments = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const data = await resourcesService.getDocuments();
      setDocuments(data.documents || []);
      setCategories(['All', ...(data.categories || [])]);
    } catch (err) {
      console.error('Error loading documents:', err);
      if (!silent) {
        setError('Failed to load documents. Please try again.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleDocumentClick = async (document) => {
    try {
      const result = await resourcesService.downloadDocument(document.filename);
      
      // If it's a PWA PDF, open the in-app viewer
      if (result.isPWA && result.pdfUrl) {
        setCurrentPdfUrl(result.pdfUrl);
        setCurrentPdfFilename(result.filename);
        setPdfViewerOpen(true);
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(`Failed to download ${document.displayName}. Please try again.`);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    if (currentPdfUrl) {
      window.URL.revokeObjectURL(currentPdfUrl);
      setCurrentPdfUrl(null);
    }
    setCurrentPdfFilename('');
  };

  const handleDownloadPdf = () => {
    if (currentPdfUrl && currentPdfFilename) {
      // Create a temporary link to download the PDF
      const link = document.createElement('a');
      link.href = currentPdfUrl;
      link.download = currentPdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleRefresh = () => {
    loadDocuments();
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      '@media (max-width:600px)': {
        paddingBottom: '88px', // 72px bottom nav height + 16px extra margin
      }
    }}>
      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Category Filter */}
      <CategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        onRefresh={handleRefresh}
        sx={{ mb: 3 }}
      />

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedCategory === 'All' 
              ? 'No documents are currently available.'
              : `No documents found in the "${selectedCategory}" category.`
            }
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredDocuments.map((document) => (
            <Grid item xs={12} sm={6} md={3} lg={2.4} key={document.filename}>
              <ResourceTile
                document={document}
                onClick={() => handleDocumentClick(document)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Document Count */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} 
          {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
        </Typography>
      </Box>

      {/* PWA PDF Viewer Modal */}
      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth={false}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            maxHeight: '100vh',
            height: '100vh',
            width: '100vw',
          }
        }}
      >
        <AppBar sx={{ 
          position: 'relative',
          paddingTop: 'env(safe-area-inset-top)', // Handle iOS notch/status bar
        }}>
          <Toolbar sx={{ 
            minHeight: { xs: 64, sm: 64 },
            height: { xs: 64, sm: 64 },
            pr: 1,
            pl: 2,
            pt: { xs: '15px', sm: 0 }, // Add 5px top padding on mobile
            display: 'flex',
            alignItems: 'center', // Center align for both mobile and desktop
            justifyContent: 'space-between'
          }}>
            <Box sx={{ 
              flex: 1, 
              mr: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center', // Center align text container
              justifyContent: 'flex-start',
              height: '100%'
            }}>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center', // Center align text
                  '@media (max-width: 600px)': {
                    whiteSpace: 'normal',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    maxHeight: '2.4em',
                    fontSize: '0.875rem',
                    // Remove bottom padding for center alignment
                  }
                }}
                title={currentPdfFilename}
              >
                {currentPdfFilename}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                color="inherit"
                onClick={handleDownloadPdf}
                aria-label="download"
                sx={{ 
                  flexShrink: 0,
                  p: { xs: 0.75, sm: 1.5 },
                  minWidth: { xs: 40, sm: 48 },
                  minHeight: { xs: 40, sm: 48 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DownloadIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </IconButton>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClosePdfViewer}
                aria-label="close"
                sx={{ 
                  flexShrink: 0,
                  p: { xs: 0.75, sm: 1.5 },
                  minWidth: { xs: 40, sm: 48 },
                  minHeight: { xs: 40, sm: 48 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ 
          p: 0, 
          height: 'calc(100vh - 64px - env(safe-area-inset-top))',
          '@supports not (padding: env(safe-area-inset-top))': {
            height: 'calc(100vh - 64px)', // Fallback for older browsers
          }
        }}>
          {currentPdfUrl && (
            <iframe
              src={currentPdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title={currentPdfFilename}
              allow="fullscreen"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Resources;
